import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { fadeInUp, fadeInLeft, fadeInRight } from '../utils/animations';

const About = () => {
  const { ref, controls } = useScrollReveal();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const PROFILE_SRC = '/profile.jpg'; // 프로젝트 루트의 public/profile.jpg 를 사용합니다.

  // Interactive logo cloud
  const containerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [clickInfo, setClickInfo] = useState(null); // {t, angles:number[]}
  const [paused, setPaused] = useState(false);
  const [blockFollow, setBlockFollow] = useState(false);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    setMousePos({ x: mouseX, y: mouseY, centerX, centerY });
  };

  const handleClick = (e) => {
    e.preventDefault();
    // Precompute random directions per icon for the burst
    const angles = logos.map(() => Math.random() * Math.PI * 2);
    setClickInfo({ t: Date.now(), angles });
  };

  // Icon defs and physics resolution
  const logos = [
    { key: 'react', src: 'https://cdn.simpleicons.org/react/61DAFB', size: 28 },
    { key: 'node', src: 'https://cdn.simpleicons.org/nodedotjs/339933', size: 28 },
    { key: 'tailwind', src: 'https://cdn.simpleicons.org/tailwindcss/38B2AC', size: 28 },
    { key: 'js', src: 'https://cdn.simpleicons.org/javascript/F7DF1E', size: 28 },
    { key: 'figma', src: 'https://cdn.simpleicons.org/figma/F24E1E', size: 28 },
    { key: 'html', src: 'https://cdn.simpleicons.org/html5/E34F26', size: 28 },
    { key: 'css', src: 'https://cdn.simpleicons.org/css/1572B6', size: 28 },
    { key: 'typescript', src: 'https://cdn.simpleicons.org/typescript/3178C6', size: 28 },
    { key: 'gitlfs', src: 'https://cdn.simpleicons.org/gitlfs/F05032', size: 28 },
    { key: 'python', src: 'https://cdn.simpleicons.org/python/3776AB', size: 28 },
    { key: 'github', src: 'https://cdn.simpleicons.org/github/181717', size: 28 },
    { key: 'git', src: 'https://cdn.simpleicons.org/git/F05032', size: 28 },
    { key: 'docker', src: 'https://cdn.simpleicons.org/docker/2496ED', size: 28 },
    { key: 'ubuntu', src: 'https://cdn.simpleicons.org/ubuntu/E95420', size: 28 },
    { key: 'prisma', src: 'https://cdn.simpleicons.org/prisma/143D53', size: 28 },
    { key: 'sqlite', src: 'https://cdn.simpleicons.org/sqlite/003B57', size: 28 },
    { key: 'ngrok', src: 'https://cdn.simpleicons.org/ngrok/02238F', size: 28 },
    { key: 'nginx', src: 'https://cdn.simpleicons.org/nginx/009639', size: 28 },
    { key: 'c', src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/18/C_Programming_Language.svg/960px-C_Programming_Language.svg.png?20201031132917', size: 28 },
    { key: 'vite', src: 'https://vite.dev/logo.svg', size: 28 },
  ];

  // 각 아이콘의 실제 충돌 반경(아이콘 크기 절반 + 여유 패딩)
  const radii = logos.map((l) => l.size / 2 + 6);

  const simRef = useRef(logos.map(() => ({ x: 0, y: 0, vx: 0, vy: 0 })));
  const lastClickRef = useRef(0);
  const burstAnglesRef = useRef(logos.map(() => Math.random() * Math.PI * 2));
  const [simTick, setSimTick] = useState(0);

  // Scatter(영역 밖) 상태에서 사용할 동적 오비트 파라미터들
  const scatterBaseAnglesRef = useRef(logos.map(() => Math.random() * Math.PI * 2));
  const scatterSpeedsRef = useRef(logos.map(() => 0.0003 + Math.random() * 0.0006)); // rad/ms
  const scatterRadiiRef = useRef(logos.map((_, i) => 450 + Math.sin(i * 1.3) * 50 + Math.random() * 40));
  const scatterCenterRef = useRef({ x: 0, y: 0 });

  const mouseRelX = (mousePos.x ?? 0) - (mousePos.centerX ?? 0);
  const mouseRelY = (mousePos.y ?? 0) - (mousePos.centerY ?? 0);

  useEffect(() => {
    let rafId;
    const step = () => {
      const followK = 0.05; // slightly stronger attraction for better control
      const friction = 0.95; // balanced friction
      const iconRadius = 18; // slightly smaller for closer positioning
      const sepK = 0.12; // moderate separation
      const mass = 1.0; // mass for realistic physics
      const mouseVelocity = Math.hypot(mouseRelX, mouseRelY); // calculate mouse movement intensity

      // Target is the mouse relative position (blocked when hovering pause button)
      const tx = blockFollow ? 0 : mouseRelX;
      const ty = blockFollow ? 0 : mouseRelY;

      if (!paused) {
        // Apply realistic physics with mass
        for (let i = 0; i < simRef.current.length; i++) {
          const p = simRef.current[i];
          // Attraction force with mass consideration
          const fx = (tx - p.x) * followK / mass;
          const fy = (ty - p.y) * followK / mass;
          p.vx += fx;
          p.vy += fy;

          // Dynamic randomness based on mouse movement intensity
          const dynamicRandomness = Math.min(0.02, mouseVelocity * 0.001);
          p.vx += (Math.random() - 0.5) * dynamicRandomness;
          p.vy += (Math.random() - 0.5) * dynamicRandomness;

          // Add some inertia from mouse movement
          if (mouseVelocity > 5) {
            p.vx += (Math.random() - 0.5) * mouseVelocity * 0.0005;
            p.vy += (Math.random() - 0.5) * mouseVelocity * 0.0005;
          }
        }
      }

      // Real collision detection using actual icon boundaries with padding
      if (!paused) {
        for (let i = 0; i < simRef.current.length; i++) {
          for (let j = i + 1; j < simRef.current.length; j++) {
            const a = simRef.current[i];
            const b = simRef.current[j];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const dist = Math.hypot(dx, dy) || 0.0001;
            // 아이콘별 반경을 합산하여 최소 거리 계산
            const ri = radii[i];
            const rj = radii[j];
            const minDist = ri + rj;

            if (dist < minDist) {
              const overlap = minDist - dist;
              // 커서(target) 근접 시 분리력을 강화하여 겹침 방지
              const da = Math.hypot(a.x - tx, a.y - ty);
              const db = Math.hypot(b.x - tx, b.y - ty);
              const near = Math.max(0, 1 - Math.min(da, db) / 180); // 180px 이내에서 near 증가
              const boost = 1 + 1.5 * near; // 최대 +150% 분리력 부스트
              const push = overlap * sepK * boost;
              const nx = dx / dist;
              const ny = dy / dist;
              a.vx -= nx * push;
              a.vy -= ny * push;
              b.vx += nx * push;
              b.vy += ny * push;
            }
          }
        }
      }

      // Apply velocity and position with physics
      if (!paused) {
        for (let i = 0; i < simRef.current.length; i++) {
          const p = simRef.current[i];
          p.vx *= friction;
          p.vy *= friction;
          p.x += p.vx;
          p.y += p.vy;
        }
      }
      
      // If mouse is outside container OR hovering the pause button, scatter icons (emergent drift, no forced orbit)
      if ((!isHovering || blockFollow) && !paused) {
        for (let i = 0; i < simRef.current.length; i++) {
          const p = simRef.current[i];
          const t = Date.now();
          const cx = scatterCenterRef.current.x;
          const cy = scatterCenterRef.current.y;
          const dx = p.x - cx;
          const dy = p.y - cy;
          const r = Math.hypot(dx, dy) || 0.0001;
          const nx = dx / r;
          const ny = dy / r;

          // 원하는 느슨한 반경으로만 살짝 보정 (스프링처럼)
          const baseR = scatterRadiiRef.current[i];
          const desiredR = baseR + 24 * Math.sin(t * 0.001 + i * 0.7);
          const radial = (desiredR - r) * 0.02; // 보정 강도 완화
          p.vx += nx * radial;
          p.vy += ny * radial;

          // 속도를 아주 미세하게 회전시켜 자연스러운 드리프트 유도 (강제 공전 아님)
          const rot = 0.003 * Math.sin(t * 0.001 + i);
          const cosr = Math.cos(rot);
          const sinr = Math.sin(rot);
          const vx0 = p.vx;
          const vy0 = p.vy;
          p.vx = vx0 * cosr - vy0 * sinr;
          p.vy = vx0 * sinr + vy0 * cosr;

          // 약간의 무작위성으로 패턴 고정 방지
          p.vx += (Math.random() - 0.5) * 0.02;
          p.vy += (Math.random() - 0.5) * 0.02;
        }
      }

      setSimTick((t) => t + 1);
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [mouseRelX, mouseRelY, isHovering, logos.length, paused, blockFollow]);

  useEffect(() => {
    if (!clickInfo) return;
    lastClickRef.current = clickInfo.t;
    // Don't change burst angles - keep them consistent for natural return
    if (!burstAnglesRef.current.length) {
      burstAnglesRef.current = logos.map(() => Math.random() * Math.PI * 2);
    }
    const impulse = 20.0; // much stronger burst
    for (let i = 0; i < simRef.current.length; i++) {
      const ang = burstAnglesRef.current[i];
      simRef.current[i].vx += Math.cos(ang) * impulse;
      simRef.current[i].vy += Math.sin(ang) * impulse;
    }
  }, [clickInfo]);

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={fadeInUp}
          className="text-center mb-20"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-neutral-800 mb-6 leading-tight tracking-tight">
            ABOUT
          </h2>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-[1.7]">
            안녕하세요! 저는 끊임없이 더 나은 서비스를 제공하기 위해 노력하는 <strong>이준성</strong> 입니다.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Image */}
          <motion.div
            initial="initial"
            animate={controls}
            variants={fadeInLeft}
            className="relative"
          >
            <div
              ref={containerRef}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
                setMousePos({ x: 0, y: 0, centerX: 0, centerY: 0 });
                // 영역을 벗어날 때 오비트 기준을 약간 랜덤화하여 매번 다른 패턴 연출
                scatterBaseAnglesRef.current = scatterBaseAnglesRef.current.map(
                  (a) => a + (Math.random() - 0.5) * 0.5
                );
                // 반경도 소폭 재샘플링해 고정된 링 느낌 완화
                scatterRadiiRef.current = scatterRadiiRef.current.map(
                  (r, i) => 450 + Math.sin(i * 1.3 + Math.random() * 0.6) * 50 + Math.random() * 40
                );
                // 중심은 이미지 중앙(0,0) 유지
                scatterCenterRef.current = { x: 0, y: 0 };
                // 즉시 바깥으로 퍼지는 가벼운 임펄스 (현재 위치 방향으로 밀어냄)
                for (let i = 0; i < simRef.current.length; i++) {
                  const p = simRef.current[i];
                  const dx = p.x - 0;
                  const dy = p.y - 0;
                  const len = Math.hypot(dx, dy) || 0.0001;
                  const ux = dx / len;
                  const uy = dy / len;
                  const impulse = 0.35; // 필요시 0.2~0.6 사이 튜닝
                  p.vx += ux * impulse;
                  p.vy += uy * impulse;
                }
              }}
              onMouseMove={handleMouseMove}
              onClick={handleClick}
              className="relative w-full h-96 bg-gradient-to-br from-primary-50 to-neutral-50 rounded-2xl overflow-hidden shadow-sm"
            >
              {/* Profile image with graceful fallback */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-48 h-48">
                  <img
                    src={PROFILE_SRC}
                    alt="Profile"
                    className={`w-48 h-48 rounded-full object-cover ${imageLoaded && !imageError ? '' : 'hidden'}`}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                    decoding="async"
                  />
                  {(!imageLoaded || imageError) && (
                    <div className="w-48 h-48 bg-primary-300 rounded-full flex items-center justify-center select-none">
                      <span className="text-6xl text-white font-bold pointer-events-none">👨‍💻</span>
                    </div>
                  )}

                  {/* Tech logos - free physics followers (no fixed anchors) */}
                  {logos.map((logo, idx) => {
                    const pos = simRef.current[idx];
                    const finalX = pos.x;
                    const finalY = pos.y;
                    return (
                      <motion.img
                        key={logo.key}
                        src={logo.src}
                        alt={logo.key}
                        width={logo.size}
                        height={logo.size}
                        className="absolute select-none pointer-events-none bg-white rounded-full p-1.5 shadow-md"
                        style={{ left: '50%', top: '50%' }}
                        initial={{ x: -logo.size/2, y: -logo.size/2, scale: 0, opacity: 0 }}
                        animate={{ x: finalX - logo.size/2, y: finalY - logo.size/2, scale: clickInfo ? 1.06 : 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: clickInfo ? 600 : 260, damping: clickInfo ? 16 : 22, mass: 0.65 }}
                      />
                    );
                  })}
                </div>
              </div>
              
                {/* Interactive hint - cursor icon */}
              <AnimatePresence>
                {!isHovering && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="absolute top-4 right-4 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center"
                  >
                    <span className="text-lg">👆</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Pause toggle button - bottom right, stays clickable; dims when hovering area */}
              <motion.button
                type="button"
                aria-label={paused ? 'Resume animation' : 'Pause animation'}
                onClick={(e) => { e.stopPropagation(); setPaused((p) => !p); }}
                onMouseEnter={() => setBlockFollow(true)}
                onMouseLeave={() => setBlockFollow(false)}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: isHovering ? 0.28 : 1, scale: 1 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="absolute bottom-4 right-4 h-9 px-3 bg-white/90 hover:bg-white rounded-full shadow-md border border-neutral-200 text-sm font-medium text-neutral-700 flex items-center gap-2"
              >
                {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </motion.button>
            </div>
          </motion.div>

          {/* Right side - Content */}
          <motion.div
            initial="initial"
            animate={controls}
            variants={fadeInRight}
            className="space-y-6"
          >
            <div>
              <h3 className="text-2xl font-bold text-neutral-800 mb-6 leading-tight">
                안녕하세요! 👋
              </h3>
              <p className="text-neutral-600 leading-[1.7] mb-6">
                저는 초등학교 5학년때 프로그래밍을 처음 접하고 계속하여 프로그램과 웹사이트를 제작하고 있어요.
                지금은 <strong>목포대학교 정보보호영재원</strong>을 수료하고, <strong>선린인터넷고등학교 소프트웨어과</strong>에 재학하면서 열심히 공부하고 있어요.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
